/**
 * Skeleton loaders — replace "Loading…" text with a mock-up of the
 * final layout so the shell doesn't jump when data arrives.
 *
 * The shimmer utility lives in `styles.css` as `.atlas-skeleton` and
 * picks up `--color-bg-secondary` / `--color-bg-tertiary` from the
 * atlas theme so it matches the current brand automatically.
 */

interface SkeletonProps {
    className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
    return <div aria-hidden="true" className={`atlas-skeleton rounded-md ${className ?? ""}`} />;
}

export function SkeletonText({ className }: SkeletonProps) {
    return <Skeleton className={`h-4 ${className ?? "w-full"}`} />;
}

// ──────────────────── Submission detail ───────────────────

/** Mirrors SubmissionNav's two-group structure so the side-nav doesn't
 *  pop in with real labels while the rest of the page is still in
 *  skeleton state. */
export function SubmissionNavSkeleton() {
    const groups = [4, 4];
    return (
        <nav className="flex w-56 shrink-0 flex-col gap-6 py-2" aria-label="Loading submission sections">
            {groups.map((itemCount, groupIdx) => (
                <div key={groupIdx} className="flex flex-col gap-1">
                    <Skeleton className="mx-3 mb-2 h-3 w-20" />
                    {Array.from({ length: itemCount }).map((_, i) => (
                        <div key={i} className="px-3 py-2">
                            <Skeleton className="h-4 w-32" />
                        </div>
                    ))}
                </div>
            ))}
        </nav>
    );
}

export function SubmissionHeaderSkeleton() {
    return (
        <header className="border-secondary flex flex-col gap-6 border-b px-8 pb-6 pt-8 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-2">
                <Skeleton className="h-7 w-28" />
                <Skeleton className="h-4 w-56" />
            </div>
            <div className="flex flex-wrap gap-4">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="flex flex-col gap-2">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-9 w-[174px]" />
                    </div>
                ))}
            </div>
        </header>
    );
}

function SkeletonFormGroup({ fields }: { fields: number }) {
    return (
        <div className="border-secondary bg-primary flex flex-col gap-5 rounded-xl border p-6">
            <Skeleton className="h-5 w-40" />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                {Array.from({ length: fields }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-2">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-9 w-full" />
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Mirror of the UW-Information page body — three stacked form groups
 *  with field-shaped skeletons so the layout doesn't jolt when the
 *  real content arrives. */
export function SubmissionDetailSkeleton() {
    return (
        <div role="status" aria-label="Loading submission" className="flex flex-col gap-6">
            <SkeletonFormGroup fields={6} />
            <SkeletonFormGroup fields={3} />
            <SkeletonFormGroup fields={3} />
        </div>
    );
}
